import { useState } from 'react';
import { ThumbsDown, ThumbsUp, Loader2 } from 'lucide-react';
import { clearAnswerFeedback, submitAnswerFeedback } from '../../services/zoeFeedbackApi';
import {
  ANSWER_FEEDBACK_REASONS,
  ANSWER_FEEDBACK_REASON_LABELS,
  type AnswerFeedbackReason,
  type AnswerRating,
} from '../../types/zoeFeedback';

interface AnswerFeedbackControlProps {
  conversationId: string;
  /** `InvestigationResponse.investigation_id` — the turn's run id. */
  runId: string;
  /** The answer text on screen, stored with the rating so reviewers see what was read. */
  answerSeen?: string;
}

const COMMENT_MAX = 2000;

/**
 * Per-answer quality signal: thumbs up saves immediately; thumbs down opens a
 * short form (reasons + optional comment) so bad answers arrive in the Truth
 * Layer → Zoe Q&A Log with enough context to act on. Clicking the active
 * thumb again withdraws the rating.
 */
export default function AnswerFeedbackControl({ conversationId, runId, answerSeen }: AnswerFeedbackControlProps) {
  const [saved, setSaved] = useState<AnswerRating | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [reasons, setReasons] = useState<AnswerFeedbackReason[]>([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (rating: AnswerRating, withDetail: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await submitAnswerFeedback({
        conversationId,
        runId,
        rating,
        ...(withDetail ? { reasons, comment: comment.trim() } : {}),
        answerSeen,
      });
      setSaved(rating);
      setFormOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save feedback');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await clearAnswerFeedback(conversationId, runId);
      setSaved(null);
      setReasons([]);
      setComment('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not clear feedback');
    } finally {
      setBusy(false);
    }
  };

  const onThumbUp = () => {
    if (busy) return;
    if (saved === 'up') return void withdraw();
    setFormOpen(false);
    void submit('up', false);
  };

  const onThumbDown = () => {
    if (busy) return;
    if (saved === 'down' && !formOpen) return void withdraw();
    setFormOpen((open) => !open);
  };

  const toggleReason = (reason: AnswerFeedbackReason) =>
    setReasons((prev) => (prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]));

  const thumbClass = (active: boolean, tone: 'up' | 'down') =>
    [
      'inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors disabled:opacity-50',
      active
        ? tone === 'up'
          ? 'border-success/60 bg-success/15 text-success'
          : 'border-destructive/60 bg-destructive/15 text-destructive'
        : 'border-border text-muted-foreground hover:text-foreground hover:border-accent',
    ].join(' ');

  return (
    <div className="rounded-lg border border-border/70 bg-card/40 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {saved === 'up'
            ? 'Thanks — marked as a good answer.'
            : saved === 'down'
              ? 'Thanks — flagged for review.'
              : 'Was this answer right?'}
        </span>
        <div className="flex items-center gap-1.5 ml-auto">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" aria-hidden /> : null}
          <button
            type="button"
            className={thumbClass(saved === 'up', 'up')}
            onClick={onThumbUp}
            disabled={busy}
            aria-pressed={saved === 'up'}
            aria-label={saved === 'up' ? 'Remove good-answer rating' : 'Good answer'}
            title={saved === 'up' ? 'Remove rating' : 'Good answer'}
          >
            <ThumbsUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            className={thumbClass(saved === 'down' || formOpen, 'down')}
            onClick={onThumbDown}
            disabled={busy}
            aria-pressed={saved === 'down'}
            aria-expanded={formOpen}
            aria-label={saved === 'down' ? 'Remove bad-answer flag' : 'Flag a bad answer'}
            title={saved === 'down' ? 'Remove flag' : 'Flag a bad answer'}
          >
            <ThumbsDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {formOpen ? (
        <form
          className="mt-3 space-y-3 border-t border-border/70 pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit('down', true);
          }}
        >
          <fieldset>
            <legend className="text-xs font-medium text-foreground mb-2">What was wrong?</legend>
            <div className="flex flex-wrap gap-2">
              {ANSWER_FEEDBACK_REASONS.map((reason) => {
                const active = reasons.includes(reason);
                return (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => toggleReason(reason)}
                    aria-pressed={active}
                    className={[
                      'rounded-full border px-3 py-1 text-xs transition-colors',
                      active
                        ? 'border-destructive/60 bg-destructive/15 text-destructive'
                        : 'border-accent text-foreground hover:border-muted-foreground',
                    ].join(' ')}
                  >
                    {ANSWER_FEEDBACK_REASON_LABELS[reason]}
                  </button>
                );
              })}
            </div>
          </fieldset>
          <label className="block">
            <span className="text-xs font-medium text-foreground">What should Zoe have said? (optional)</span>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
              rows={3}
              className="mt-1 w-full rounded-md border border-accent bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:border-destructive focus:outline-none"
              placeholder="e.g. The order was delivered to Leeds, not Manchester."
            />
          </label>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              className="rounded-md px-3 py-1.5 text-xs text-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              Send feedback
            </button>
          </div>
        </form>
      ) : null}

      {error ? <p className="mt-2 text-xs text-destructive" role="alert">{error}</p> : null}
    </div>
  );
}
